import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardBody, Col, Input, Row, Spinner } from 'reactstrap';
import {
  aggregateOpenPlay,
  fmtInt,
  fmtTime,
  palette,
  pivotWatch,
  sumOpenPlayDaily,
  toISODate,
} from '../../../lib/dashboardUtils';
import { getDashboardOverviewData } from '../../../services/api';
import { OPPoint, OverviewResponse } from '../../../types/Dashboard';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  CartesianGrid,
  Label,
  LineChart,
  Line,
  ComposedChart,
} from 'recharts';
import { useTranslation } from 'react-i18next';

import Error404 from '../../pages/others/errorPage/Error404';
import { CustomLoader } from '../../uiKits/customLoader/CustomLoader';

const Overview = () => {
  const today = useMemo(() => new Date(), []);
  const startDefault = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  }, []);

  const [from, setFrom] = useState<string>(toISODate(startDefault));
  const [to, setTo] = useState<string>(toISODate(today));
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [watchMode, setWatchMode] = useState<'video' | 'course'>('course');
  const [groupBy, setGroupBy] = useState<'video' | 'course' | 'lesson'>('video');
  const { t } = useTranslation();
  const fetchData = async (fromDate: string, toDate: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDashboardOverviewData(fromDate, toDate);

      setData(res);
    } catch (e: any) {
      setError(e?.message ?? t('global.failedLoad'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData(from, to);
  }, []);

  const pivoted = useMemo(() => {
    if (!data) return { data: [], keys: [] as string[] };
    return watchMode === 'video'
      ? pivotWatch(data.charts.watchByDayVideo, 'lesson_title')
      : pivotWatch(data.charts.watchByDayCourse, 'course_name');
  }, [data, watchMode]);

  const openPlaySource = useMemo(() => {
    if (!data) return [];
    switch (groupBy) {
      case 'course':
        return data.charts.openVsPlayByCourse;
      case 'lesson':
        return data.charts.openVsPlayByLesson;
      default:
        return data.charts.openVsPlayByVideo;
    }
  }, [data, groupBy]);

  const openPlayDaily = useMemo(() => sumOpenPlayDaily(openPlaySource), [openPlaySource]);

  if (loading) {
    return (
      <Col xl="12" className="h-screen box-col-12 proorder-md-1">
        <Card className={`relative min-h-[400px]`}>
          <CardBody className={`premium-card flex justify-center items-center h-full`}>
            <CustomLoader />
          </CardBody>
        </Card>
      </Col>
    );
  }
  if (error) return <Error404 />;
  return (
    <Col xl="12" className="box-col-12 proorder-md-1">
      <Card className={`relative `}>
        <CardBody className={`premium-card `}>
          {/* Controls */}
          <Row className="align-items-end g-3 mb-3">
            {/* <CustomLoader /> */}
            <Col md="3">
              <Label className="form-label">{t('stats.from')}</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Col>
            <Col md="3">
              <Label className="form-label">{t('stats.to')}</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Col>
            <Col md="2" className="pt-1">
              <Button color="primary" onClick={() => fetchData(from, to)} disabled={loading}>
                {loading ? <Spinner size="sm" /> : t('stats.refresh')}
              </Button>
            </Col>
            {error && (
              <Col md="12" className="text-danger">
                {error}
              </Col>
            )}
          </Row>
          {/* KPI Cards */}
          <Row className="g-3 mb-4">
            <KpiCard
              title={t('stats.dau')}
              value={fmtInt(data?.kpis.dau ?? 0)}
              hint={t('stats.dauHint')}
            />
            <KpiCard
              title={t('stats.wau')}
              value={fmtInt(data?.kpis.wau ?? 0)}
              hint={t('stats.wauHint')}
            />
            <KpiCard
              title={t('stats.mau')}
              value={fmtInt(data?.kpis.mau ?? 0)}
              hint={t('stats.mauHint')}
            />
            <KpiCard title={t('stats.newStudents')} value={fmtInt(data?.kpis.new_students ?? 0)} />
            <KpiCard
              title={t('stats.avgSessionStudent')}
              value={(data?.kpis.avg_sessions_per_student ?? 0).toFixed(2)}
            />
            <KpiCard
              title={t('stats.avgWatchActive')}
              value={fmtTime(data?.kpis.avg_watch_ms_per_active ?? 0, t)}
            />
            <KpiCard
              title={t('stats.onlineNow')}
              value={fmtInt(data?.kpis.online_now ?? 0)}
              accent="success"
            />
          </Row>
          {/* Daily Active */}
          <Row className="g-4">
            <Col xl="6" md="12">
              <Section title={t('stats.dailyActiveStudents')}>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart
                    data={data?.charts.dailyActive ?? []}
                    margin={{ left: 8, right: 16, top: 10, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="gradDAU" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                        <stop offset="90%" stopColor="#3b82f6" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <Tooltip labelStyle={{ fontWeight: 600 }} />
                    <Area
                      type="monotone"
                      dataKey="users"
                      stroke="#3b82f6"
                      fill="url(#gradDAU)"
                      name={t('stats.charts.students')}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Section>
            </Col>
            {/* Watch time stacked */}
            <Col xl="6" md="12">
              <Section
                title={
                  <div className="d-flex align-items-center gap-3">
                    <span>{t('stats.totalWatchPerDay')}</span>
                    <div className="ms-auto d-flex align-items-center gap-2">
                      <small>{t('stats.stackBy')}</small>
                      <Input
                        type="select"
                        value={watchMode}
                        onChange={(e) => setWatchMode(e.target.value as any)}
                        style={{ width: 140 }}
                      >
                        <option value="course">{t('stats.course')}</option>
                        <option value="video">{t('stats.video')}</option>
                      </Input>
                    </div>
                  </div>
                }
              >
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={pivoted.data} margin={{ left: 8, right: 16, top: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {pivoted.keys.map((k, idx) => (
                      <Bar
                        key={k}
                        dataKey={k}
                        stackId="watch"
                        fill={palette[idx % palette.length]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Section>
            </Col>
          </Row>
          {/* Open vs Play (aggregated by day) */}
          {openPlayDaily && (
            <Row className="g-4 mt-1">
              <Col xl="12" md="12">
                <Section
                  title={
                    <div className="d-flex align-items-center gap-3">
                      <span>{t('stats.openVsPlay')}</span>
                      <div className="ms-auto d-flex align-items-center gap-2">
                        <small>{t('stats.groupBy')}</small>
                        <Input
                          type="select"
                          value={groupBy}
                          onChange={(e) => setGroupBy(e.target.value as any)}
                          style={{ width: 160 }}
                        >
                          <option value="video">{t('stats.video')}</option>
                          <option value="course">{t('stats.course')}</option>
                          <option value="lesson">{t('stats.lesson')}</option>
                        </Input>
                      </div>
                    </div>
                  }
                >
                  {/* <ResponsiveContainer width="100%" height={280}> */}
                  <ComposedOpenPlay data={openPlayDaily} />
                  {/* </ResponsiveContainer> */}
                </Section>
              </Col>
            </Row>
          )}
          {/* Event mix */}
          <Row className="g-4 mt-1">
            <Col xl="12" md="12">
              <Section title={t('stats.charts.eventMix')}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={data?.charts.eventMix ?? []}
                    margin={{ left: 8, right: 16, top: 10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="event" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </Section>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </Col>
  );
};

const KpiCard: React.FC<{
  title: string;
  value: string | number;
  accent?: 'success' | 'danger' | 'warning' | 'info';
  hint?: string;
}> = ({ title, value, accent, hint }) => (
  <Col xl="3" md="4" sm="6">
    <Card className={`shadow-sm border-0 ${accent ? `border-${accent}` : ''}`}>
      <CardBody>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {title}
        </div>
        <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
        {hint ? (
          <div className="text-muted" style={{ fontSize: 12 }}>
            {hint}
          </div>
        ) : null}
      </CardBody>
    </Card>
  </Col>
);

const Section: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Card className="shadow-sm border-0">
    <CardBody>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>{title}</div>
      {children}
    </CardBody>
  </Card>
);
const ComposedOpenPlay: React.FC<{ data: OPPoint[] }> = ({ data }) => {
  const { t } = useTranslation();
  const normalized = useMemo(
    () =>
      (data ?? [])
        .map((d) => ({
          date: d.date,
          opens_sessions: Number(d.opens_sessions) || 0,
          plays_sessions: Number(d.plays_sessions) || 0,
          play_rate: Number(d.play_rate) || 0,
        }))
        .sort((a, b) => a.date.localeCompare(b.date)),
    [data],
  );

  // Force ResponsiveContainer to recalc after data appears or container becomes visible
  useEffect(() => {
    const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
    return () => clearTimeout(t);
  }, [normalized.length]);

  if (!normalized.length) {
    return (
      <div
        style={{
          height: 280,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9ca3af',
        }}
      >
        {t('stats.noData')}
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: 280 }}>
      <ResponsiveContainer width="100%" height="100%" debounce={150}>
        <ComposedChart data={normalized} margin={{ left: 8, right: 16, top: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12 }}
            allowDecimals={false}
            domain={[0, 'dataMax + 1']}
          />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} domain={[0, 100]} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar
            yAxisId="left"
            dataKey="opens_sessions"
            name={t('stats.charts.opensSessions')}
            fill="#9ca3af"
            isAnimationActive={false}
          />
          <Bar
            yAxisId="left"
            dataKey="plays_sessions"
            name={t('stats.charts.playsSessions')}
            fill="#3b82f6"
            isAnimationActive={false}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="play_rate"
            name={t('stats.charts.playRate')}
            stroke="#ef4444"
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
export default Overview;
